import { useFormik } from 'formik'
import * as React from 'react'
import { useDebounce } from 'react-use'

import { useInternationalAvailability } from '../../../../../app/_hooks/useInternationalAvailability'
import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { useTrackSearchResults } from '../../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useGoogleTagManager } from '../../../../../lib/hooks/useGoogleTagManager'
import { useSearchAutocomplete } from '../../../../../lib/hooks/useSearchAutocomplete'
import { SearchAutocompleteProduct } from '../../../../../types/graphql'

interface ISearchOverlayFormValues {
  searchText: string
}

function noOp() {}

export interface ExposedMethods {
  clearSearchResults(): void
}

export interface Args {
  focusOnMount: boolean
  initialSearchText: string
  isMobileSearch?: boolean
  onAutoComplete?: (hasResults: boolean) => void
  onClearFormClick?: () => void
  onClickSearchOverlayProduct?: () => void
  onFormSubmit: (searchText: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ref: React.MutableRefObject<any> | ((instance: unknown) => void) | null
  shouldDisplaySearchOverlay: boolean
}

export function useViewModel({
  focusOnMount,
  initialSearchText,
  isMobileSearch,
  onAutoComplete = noOp,
  onClickSearchOverlayProduct,
  onClearFormClick = noOp,
  onFormSubmit,
  ref,
  shouldDisplaySearchOverlay,
}: Args) {
  const {
    error: searchError,
    fetchSearchAutocompleteResults,
    products: searchProducts,
    suggestions: searchSuggestions,
    noResult,
  } = useSearchAutocomplete()

  const { region } = useLocalization()
  const { search: searchCopy } = useTranslation()
  const { pushSearchSubmitted } = useGoogleTagManager()
  const { filterUnavailableItemsFromArray } = useInternationalAvailability()
  const { trackSearchResults } = useTrackSearchResults()

  // We don’t want to show autocomplete results if the input has not been edited:
  const [inputEdited, setInputEdited] = React.useState<boolean>(false)

  const hasError = !!searchError

  const [hideSearchResults, setHideSearchResults] = React.useState<boolean>(false)

  // If we have new initial search text we consider the input clean again:
  React.useEffect(() => {
    setInputEdited(false)
  }, [initialSearchText])

  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (focusOnMount && inputRef.current && shouldDisplaySearchOverlay) {
      inputRef.current.focus()
    }
  }, [focusOnMount, inputRef, shouldDisplaySearchOverlay])

  const initialValues: ISearchOverlayFormValues = React.useMemo(
    () => ({
      searchText: initialSearchText,
    }),
    [initialSearchText]
  )

  const formik = useFormik<ISearchOverlayFormValues>({
    enableReinitialize: true,
    initialValues,
    onSubmit(values) {
      onFormSubmit(values.searchText)
      pushSearchSubmitted(values.searchText)
    },
    validate(values) {
      const errors: Partial<ISearchOverlayFormValues> = {}
      if (!values.searchText || !values.searchText.trim()) {
        errors.searchText = searchCopy.inputRequiredMessage
      }
      return errors
    },
  })

  const showSearchResults = !hideSearchResults && !hasError
  const products = React.useMemo((): SearchAutocompleteProduct[] => {
    return showSearchResults
      ? filterUnavailableItemsFromArray({
          items: searchProducts,
          regionId: region.id,
        })
      : []
  }, [showSearchResults, searchProducts, region.id, filterUnavailableItemsFromArray])

  const suggestions = showSearchResults ? searchSuggestions : []

  const isInputInvalid: boolean = React.useMemo((): boolean => {
    return !!formik.touched.searchText && !!formik.errors.searchText
  }, [formik.errors.searchText, formik.touched.searchText])

  const clearSearchResults = React.useCallback(() => {
    setHideSearchResults(true)
  }, [setHideSearchResults])

  const resetForm = React.useCallback(() => {
    formik.resetForm({ values: { searchText: '' } })
    clearSearchResults()
    onClearFormClick()
    inputRef.current?.focus()
  }, [clearSearchResults, formik, onClearFormClick])

  let searchQueryHolder = React.useRef('')
  const fetchSearchResults = React.useCallback(() => {
    try {
      const query: string = formik.values.searchText
      if (!query || !query.trim() || !inputEdited) return

      fetchSearchAutocompleteResults({
        variables: { query, first: 6 },
      }).then(() => {
        if (searchQueryHolder.current !== query.trim()) {
          searchQueryHolder.current = query.trim()
        }
      })

      setHideSearchResults(false)
      onAutoComplete(Boolean(products.length || suggestions.length))
    } catch (_error) {}
  }, [
    formik.values.searchText,
    inputEdited,
    fetchSearchAutocompleteResults,
    setHideSearchResults,
    onAutoComplete,
    products.length,
    suggestions.length,
  ])

  const [, cancelInputDebounce] = useDebounce(fetchSearchResults, 500, [fetchSearchResults])

  let trackingRefHolder = React.useRef('')
  React.useEffect(() => {
    if (trackingRefHolder.current !== searchQueryHolder.current) {
      trackSearchResults({
        id: `header_`,
        query: searchQueryHolder.current,
        searchType: 'header',
        topResults: products.map(product => {
          return { handle: product.handle }
        }),
        totalResults: products.length,
      })
      trackingRefHolder.current = searchQueryHolder.current
    }
  }, [trackSearchResults, products, searchQueryHolder, inputEdited])

  React.useImperativeHandle(ref, () => ({
    clearSearchResults() {
      cancelInputDebounce()
      clearSearchResults()
    },
  }))

  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      cancelInputDebounce()
      clearSearchResults()
      formik.handleSubmit()
    },
    [cancelInputDebounce, clearSearchResults, formik]
  )

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputEdited(true)
      formik.handleChange(event)
    },
    [formik]
  )

  const handleClickSearchOverlayProduct = React.useCallback(({}: { id: string; query: string }) => {
    if (onClickSearchOverlayProduct) {
      onClickSearchOverlayProduct()
    }
    if (isMobileSearch) {
      resetForm()
    }
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showNoResultsMessage: boolean | undefined =
    noResult && formik.values.searchText.length > 0 && shouldDisplaySearchOverlay

  return {
    autoCompleteResults: products,
    handleFormChange: handleChange,
    handleSubmit,
    hasError,
    form: formik,
    inputPlaceholder: searchCopy.inputPlaceholder,
    inputRef,
    isInputInvalid,
    onClearFormClick,
    onClickSearchOverlayProduct: handleClickSearchOverlayProduct,
    popularSearchResults: suggestions,
    popularSearchTitle: searchCopy.popularSearchTitle,
    resetForm,
    searchButtonAriaLabelClear: searchCopy.searchButtonAriaLabelClear,
    searchButtonAriaLabelSubmit: searchCopy.searchButtonAriaLabelSubmit,
    searchTextError: formik.errors.searchText,
    searchTextValue: formik.values.searchText,
    showNoResultsMessage,
  }
}
